import { useAtom } from 'jotai';
import { useState } from 'react';
import { Badge, Button, Flex, Link, Switch, Text, Tooltip } from '@optiaxiom/react';
import {
  IconAgent,
  IconArrowsRotate,
  IconCheckCircleSolid,
  IconCodeBranch,
  IconSparkles,
  IconTag,
  IconToolbox,
  IconXmark,
} from '@optimizely/axiom-icons';

import { opalUserAtom } from '@frontend/atoms';
import { Sidebar } from '@frontend/components/sidebar/Sidebar';
import { useInstanceId } from '@frontend/hooks';

import {
  useAgentDirectoryDetails,
  useInstallAgentFromDirectory,
} from '../../hooks/useAgentDirectory';
import { DirectoryAgent } from '../../services/agent-directory';
import { TagDisplay } from '../specialized-agent/agent-component-view/TagDisplay';

import { styles } from './AgentDetailsSidebar.css';
import { AgentDetailsSidebarSkeleton } from './AgentDetailsSidebarSkeleton';

type AgentDetailsSidebarProps = {
  agent: DirectoryAgent | undefined;
  open: boolean;
  onClose: () => void;
};

const AgentTypeIcon = {
  specialized: <IconAgent size="16" />,
  workflow: <IconCodeBranch size="16" />,
};

export function AgentDetailsSidebar({ agent, open, onClose }: AgentDetailsSidebarProps) {
  const [opalUser] = useAtom(opalUserAtom);
  const { instanceId } = useInstanceId();
  const [forceUpdate, setForceUpdate] = useState(false);

  const { data: agentDetails, isPending } = useAgentDirectoryDetails(
    agent?.agent_id,
    agent?.directory_version
  );

  const { mutate: installAgent, isPending: isInstalling } = useInstallAgentFromDirectory();

  const agentIcon = agent ? AgentTypeIcon[agent.agent_type] || <IconSparkles size="16" /> : null;

  const handleInstall = () => {
    if (!agent || !instanceId || !opalUser?.id) return;

    installAgent({
      agentId: agent.agent_id,
      agentName: agentDetails?.name,
      request: {
        opal_instance_id: instanceId,
        user_id: opalUser.id,
        directory_version: agent.directory_version,
        force_update: forceUpdate,
      },
    });
  };

  return (
    <Sidebar open={open} dock="right" initialWidth={420} minWidth={300} maxWidth={600}>
      {isPending ? (
        <AgentDetailsSidebarSkeleton />
      ) : (
        <Flex flexDirection="column" h="full">
          {/* Main Content */}
          <Flex flexDirection="column" gap="16" p="24" flex="1" overflow="auto">
            {/* Header */}
            <Flex flexDirection="row" justifyContent="space-between" alignItems="start">
              <Flex flexDirection="row" alignItems="start" gap="12">
                <Flex className={styles.iconWrapper}>
                  <Flex
                    p="12"
                    rounded="md"
                    bg="bg.avatar.purple"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {agentIcon}
                  </Flex>
                  {agent?.is_installed &&
                    (agent.installed_version !== null &&
                    agent.installed_version < agent.directory_version ? (
                      <Tooltip content="Update available">
                        <IconArrowsRotate className={styles.updateAvailable} />
                      </Tooltip>
                    ) : (
                      <Tooltip content="Activated">
                        <IconCheckCircleSolid className={styles.installed} />
                      </Tooltip>
                    ))}
                </Flex>
                <Flex flexDirection="column" gap="0">
                  <Text fontSize="lg" fontWeight="600" color="fg.default" className={styles.title}>
                    {agentDetails?.name || 'Agent Details'}
                  </Text>
                  <Text fontSize="sm" color="fg.secondary" className={styles.title}>
                    @{agent?.agent_id || 'Unknown Agent ID'}
                  </Text>
                </Flex>
              </Flex>
              <Button
                icon={<IconXmark size="16" />}
                appearance="subtle"
                onClick={onClose}
                aria-label="Close sidebar"
              />
            </Flex>

            {/* Content */}
            {agent && agentDetails ? (
              <Flex flexDirection="column" gap="16">
                {/* Description */}
                <Flex flexDirection="column" gap="8">
                  <Flex className={styles.badgeWrapper}>
                    {agent.is_installed && <Badge intent="success">Activated</Badge>}
                  </Flex>
                  <Text fontSize="sm" color="fg.default">
                    {agentDetails.description || 'No description available'}
                  </Text>
                </Flex>

                {agentDetails?.details_url && (
                  <Flex flexDirection="row" alignItems="center" gap="12" py="8">
                    <Link href={agentDetails.details_url} external fontSize="sm">
                      View Documentation
                    </Link>
                  </Flex>
                )}

                {/* Tags and Enabled Tools */}
                <Flex flexDirection="column" gap="8" w="full">
                  {agentDetails.tags && agentDetails.tags.length > 0 && (
                    <Flex flexDirection="row" gap="8" alignItems="center" w="full">
                      <Tooltip content="Tags">
                        <IconTag size="16" color="fg.secondary" />
                      </Tooltip>
                      <TagDisplay tags={agentDetails.tags} />
                    </Flex>
                  )}
                  {agentDetails?.enabled_tools && agentDetails.enabled_tools.length > 0 && (
                    <Flex flexDirection="row" gap="8" alignItems="center" w="full">
                      <Tooltip content="Enabled Tools">
                        <IconToolbox size="16" color="fg.secondary" />
                      </Tooltip>
                      <TagDisplay tags={agentDetails.enabled_tools.map(tool => ({ name: tool }))} />
                    </Flex>
                  )}
                </Flex>

                {/* Installation Options */}
                <Flex flexDirection="column" gap="8" pt="16">
                  <Text fontSize="sm" fontWeight="500" color="fg.secondary">
                    Installation Options
                  </Text>
                  <Switch checked={forceUpdate} onCheckedChange={setForceUpdate}>
                    Update & replace existing agent
                  </Switch>
                </Flex>
              </Flex>
            ) : (
              <Text color="fg.secondary">Failed to load agent details</Text>
            )}
          </Flex>

          {/* Footer */}
          {agent && agentDetails && (
            <Flex
              flexDirection="row"
              justifyContent="flex-end"
              alignItems="center"
              gap="8"
              p="24"
              pt="16"
            >
              <Button
                appearance="primary"
                onClick={handleInstall}
                disabled={isInstalling || !instanceId || !opalUser?.id}
              >
                {isInstalling
                  ? 'Installing...'
                  : agent.is_installed
                    ? 'Update Agent'
                    : 'Activate Agent'}
              </Button>
              <Button appearance="default" onClick={onClose}>
                Close
              </Button>
            </Flex>
          )}
        </Flex>
      )}
    </Sidebar>
  );
}
